import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import usePostApprovalPlaidOffer from "../../../hooks/usePostApprovalPlaidOffer";
import useRouteStatus from "../../../hooks/useRouteStatus";
import Loading from "../../shared/Loading";
import PostApprovalPlaidConnect from "./PostApprovalPlaidConnect";
import PostApprovalPlaidSkip from "./PostApprovalPlaidSkip";
import { ApprovalBadge } from "./PostApprovalPresentation";

const renderSeam = (renderer, fallback) => {
  if (!renderer) return fallback;
  return typeof renderer === "function" ? renderer() : renderer;
};

const NeutralPanel = ({ children }) => (
  <div aria-live="polite" className="my-6 text-center">
    <p>{children}</p>
  </div>
);

const ContinueButton = ({ onClick }) => (
  <button
    className="mt-4 font-semibold text-primary-blue underline"
    onClick={onClick}
    type="button"
  >
    Continue
  </button>
);

export default function PostApprovalPlaidOffer({
  location = { pathname: "" },
  renderProcessing,
  renderTerminal,
}) {
  const plaid = usePostApprovalPlaidOffer();
  const history = useHistory();
  const attemptStatus = plaid.attempt?.get?.("status") ?? plaid.attempt?.status;
  const session = useSelector((state) => state.sessions.get("session"));
  const publicKey = useSelector((state) => state.sessions.get("public_key"));
  const contextKey = JSON.stringify([
    publicKey,
    session.get("id"),
    session.get("application_id"),
  ]);
  const processingOwner = useRef({ key: contextKey, started: false });
  if (processingOwner.current.key !== contextKey)
    processingOwner.current = { key: contextKey, started: false };
  if (plaid.offerView === "processing") processingOwner.current.started = true;
  if (plaid.offerView === "offer" || attemptStatus === "skipped")
    processingOwner.current.skipMounted = true;
  const showSkip =
    plaid.offerView === "offer" ||
    (plaid.offerView === "terminal" && attemptStatus === "skipped");
  const ownsProcessing =
    renderProcessing &&
    attemptStatus !== "skipped" &&
    (processingOwner.current.started || plaid.offerView === "terminal");

  useRouteStatus(location);

  const continueToNextRoute = () => {
    Promise.resolve(plaid.continueToNextRoute(history)).catch(() => undefined);
  };
  const retryStatus = () => {
    Promise.resolve(plaid.refreshStatus()).catch(() => undefined);
  };
  const retryEntities = () => {
    Promise.resolve(plaid.refreshEntities()).catch(() => undefined);
  };

  const content = () => {
    if (ownsProcessing) return renderSeam(renderProcessing);
    if (plaid.offerView === "loading") {
      return <Loading className="w-14 h-14" />;
    }
    if (
      plaid.offerView === "processing" ||
      (location.pathname === "/post_approval/processing" &&
        plaid.offerView === "terminal" &&
        attemptStatus !== "skipped")
    ) {
      return renderSeam(
        renderProcessing,
        <NeutralPanel>Your bank connection is processing.</NeutralPanel>
      );
    }
    if (plaid.offerView === "terminal") {
      if (attemptStatus === "skipped") {
        return null;
      }
      if (plaid.phase === "entity_refresh_error") {
        return (
          <NeutralPanel>
            We couldn't update your application.{" "}
            <button
              className="font-semibold text-primary-blue underline"
              onClick={retryEntities}
              type="button"
            >
              Try again
            </button>
          </NeutralPanel>
        );
      }
      if (plaid.phase !== "terminal") {
        return <NeutralPanel>Updating your approval.</NeutralPanel>;
      }
      return renderSeam(
        renderTerminal,
        <NeutralPanel>
          Your bank connection has been processed.
          <ContinueButton onClick={continueToNextRoute} />
        </NeutralPanel>
      );
    }
    if (plaid.offerView === "unavailable") {
      return (
        <NeutralPanel>
          Bank connection is not available for this approval.
          <ContinueButton onClick={continueToNextRoute} />
        </NeutralPanel>
      );
    }
    if (plaid.offerView === "error") {
      return (
        <NeutralPanel>
          We could not load the optional bank connection at this time.
          <button
            className="mt-4 font-semibold text-primary-blue underline"
            onClick={retryStatus}
            type="button"
          >
            Try again
          </button>
        </NeutralPanel>
      );
    }

    return (
      <>
        <ApprovalBadge />
        <h1>You're approved!</h1>
        <p>
          Link your bank account now to set up payments and see what credit
          limit and products you qualify for.
        </p>
        <ul className="post-approval-benefits">
          {[
            "Faster payments — no routing or account numbers to enter",
            "Receive your credit limit",
            "Unlock virtual card*, Credit Key Card*, and credit limit increases.",
          ].map((benefit) => (
            <li key={benefit}>
              <span aria-hidden="true" className="post-approval-check">
                ✓
              </span>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
        <PostApprovalPlaidConnect />
      </>
    );
  };

  return (
    <section
      aria-label="Approved"
      data-testid="post-approval-offer"
      className={plaid.offerView === "offer" ? "post-approval-card" : undefined}
    >
      {content()}
      {processingOwner.current.skipMounted && (
        <div key="skip-owner" className="w-full" hidden={!showSkip}>
          <PostApprovalPlaidSkip />
        </div>
      )}
      {plaid.offerView === "offer" && (
        <p className="text-xs text-tertiary-gray">
          *Subject to credit eligibility
        </p>
      )}
    </section>
  );
}
